/**
 * Postcard encoders for the credential-lifecycle wire legs: the trailing
 * `Option<SigningCred>` + `Option<MlDsaLeg>` on `ActionEnvelope`, and the
 * BARE `SigningCred` form the HTTP header (`x-signing-cred`) + WS
 * `signing_cred` JSON field carry.
 *
 * EMPIRICALLY VERIFIED postcard byte layout (cargo probe, matching the
 * Rust `arkhe-holdem-actions::credential` serde derive):
 *
 *   postcard(SigningCred) = vu32(epoch) || kind_index_byte || pubkey[32]
 *     - kind_index_byte = the POSTCARD VARIANT INDEX of `CredentialKind`:
 *       0x00 = Ed25519, 0x01 = MlDsa. NOT the serde wire string
 *       ("ed25519"/"ml_dsa") — that string is only the PG / JSON form.
 *   Option<SigningCred> = 0x00 (None) | 0x01 || postcard(SigningCred) (Some)
 *
 *   postcard(MlDsaLeg) = vu32(epoch) || vu32(sig.len()) || sig[len]
 *     - sig.len() is always 3309 for ML-DSA-65 -> vu32 = 0xed,0x19.
 *   Option<MlDsaLeg> = 0x00 (None) | 0x01 || postcard(MlDsaLeg) (Some)
 *
 * The BARE form (no Option prefix) is what the BE `parse_signing_cred_header`
 * (`postcard::from_bytes::<SigningCred>`) + the WS `signing_cred` JSON field
 * (hex of `postcard(SigningCred)`) expect — NOT the 0x01-prefixed envelope
 * form.
 */

import { concatBytes, vu64 } from "./bytes";

/** `CredentialKind` postcard variant index. */
export const CRED_KIND_INDEX_ED25519 = 0x00;
export const CRED_KIND_INDEX_ML_DSA = 0x01;

/** ML-DSA-65 FIPS-204 encoded signature length (§4). */
export const ML_DSA_65_SIG_LEN = 3309;

export type WireCredKind = "ed25519" | "ml_dsa";

export interface SigningCredFields {
  /** Credential epoch (DB-current claim the BE reconciles). */
  readonly epoch: number;
  readonly kind: WireCredKind;
  /** 32-byte pubkey (ed25519 verifying key). The ml-dsa 1952 B vk is
   *  never carried on the wire — it is DB-resolved — so `SigningCred`
   *  always carries a 32-byte key. */
  readonly pubkey: Uint8Array;
}

function kindIndex(kind: WireCredKind): number {
  return kind === "ed25519" ? CRED_KIND_INDEX_ED25519 : CRED_KIND_INDEX_ML_DSA;
}

/**
 * BARE `postcard(SigningCred)` — `vu32(epoch) || kind_index || pubkey[32]`.
 * This is the form the HTTP `x-signing-cred` header + the WS `signing_cred`
 * field carry (hex-encoded). NOT Option-wrapped.
 */
export function encSigningCredBare(cred: SigningCredFields): Uint8Array {
  if (cred.pubkey.length !== 32) {
    throw new RangeError(
      `encSigningCredBare: pubkey must be 32 bytes, got ${cred.pubkey.length}`,
    );
  }
  return concatBytes(
    vu64(BigInt(cred.epoch)),
    new Uint8Array([kindIndex(cred.kind)]),
    cred.pubkey,
  );
}

/**
 * `Option<SigningCred>` for the `ActionEnvelope` trailing positional field.
 * `null` => `0x00` (None); a value => `0x01 || encSigningCredBare`.
 */
export function encSigningCredOption(
  cred: SigningCredFields | null,
): Uint8Array {
  if (cred === null) return new Uint8Array([0x00]);
  return concatBytes(new Uint8Array([0x01]), encSigningCredBare(cred));
}

export interface MlDsaLegFields {
  readonly epoch: number;
  /** The 3309-byte ML-DSA-65 signature over the canonical message. */
  readonly sig: Uint8Array;
}

/**
 * `Option<MlDsaLeg>` for the `ActionEnvelope` trailing positional field.
 * `null` => `0x00` (None); a value => `0x01 || vu32(epoch) ||
 * vu32(sig.len()) || sig`.
 */
export function encMlDsaLegOption(leg: MlDsaLegFields | null): Uint8Array {
  if (leg === null) return new Uint8Array([0x00]);
  if (leg.sig.length !== ML_DSA_65_SIG_LEN) {
    throw new RangeError(
      `encMlDsaLegOption: sig must be ${ML_DSA_65_SIG_LEN} bytes, got ${leg.sig.length}`,
    );
  }
  return concatBytes(
    new Uint8Array([0x01]),
    vu64(BigInt(leg.epoch)),
    vu64(BigInt(leg.sig.length)),
    leg.sig,
  );
}
